import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  TextField,
  Snackbar,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import Loader from "../../../common/loader";
import ConfirmDeleteDialog from "../../../common/confirmDeleteAlert";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import BusinessIcon from "@mui/icons-material/Business";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PercentIcon from "@mui/icons-material/Percent";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useParams, useNavigate } from "react-router-dom";
import { getBankerDealDetails, createBankerDocument, getBankerDocuments, deleteBankerDocument } from "../../../services/banker.service";
import { openBusinessDocumentFile } from "../../../services/customer.service";
import ExpressInterestDialog from "./ExpressInterestDialog";
import useExpressInterest from "./useExpressInterest";
import { LoanStatus } from "../../../constants/general.constant";
import TermsSheet from "./termSheet";
import { Deal } from "../../../validation/customer.validation";
import {formatPhoneNumber} from "../../../helper/phoneFormatter.helper";
import {formatCurrency} from "../../../helper/currencyFormatter.helper";
import GuidedTutorialOverlay, {
  GuidedTutorialStep,
} from "../../common/GuidedTutorialOverlay";

const BANKER_DEAL_TUTORIAL_KEY = "bankerDealDetailsTutorialSeen";

type BankerDocumentForm = {
  docName: string;
  file?: File;
  fileName?: string;
  existingFilePath?: string;
  existingOriginalName?: string;
  type: 'upload' | 'request';
};

// Reusable InfoItem component for consistent field display
const InfoItem: React.FC<{
  icon: React.ReactElement;
  label: string;
  value: string;
  tooltip?: string;
}> = ({ icon, label, value, tooltip }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.5,
      p: 2,
      minWidth: 0,
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "background.paper",
    }}
  >
    <Tooltip title={tooltip || label}>
      <Box
        sx={{
          color: "primary.main",
          display: "flex",
          mt: 0.25,
          "& .MuiSvgIcon-root": { color: "inherit" },
        }}
      >
        {icon}
      </Box>
    </Tooltip>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 700,
          overflowWrap: "anywhere",
          color: "text.primary",
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

const MetricCard: React.FC<{
  icon: React.ReactElement;
  label: string;
  value: string;
  tone?: "blue" | "green" | "amber" | "slate";
}> = ({ icon, label, value, tone = "blue" }) => {
  const tones = {
    blue: { bg: "#eff6ff", border: "#bfdbfe", color: "#2563eb" },
    green: { bg: "#ecfdf5", border: "#bbf7d0", color: "#059669" },
    amber: { bg: "#fffbeb", border: "#fde68a", color: "#d97706" },
    slate: { bg: "#f8fafc", border: "#e2e8f0", color: "#475569" },
  };
  const selectedTone = tones[tone];

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: selectedTone.border,
        bgcolor: selectedTone.bg,
        minWidth: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box sx={{ color: selectedTone.color, display: "flex" }}>{icon}</Box>
        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700 }}>
          {label}
        </Typography>
      </Box>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 900,
          color: "text.primary",
          overflowWrap: "anywhere",
          lineHeight: 1.15,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const BankerDealDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const sectionAccordionSx = {
    mb: 2.5,
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    boxShadow: "none",
    overflow: "hidden",
    "&:before": { display: "none" },
  };
  const sectionSummarySx = {
    bgcolor: "#f8fafc",
    color: "text.primary",
    borderBottom: "1px solid",
    borderColor: "divider",
    minHeight: 56,
    "&:hover": { bgcolor: "#f1f5f9" },
    "& .MuiAccordionSummary-content": {
      alignItems: "center",
      gap: 1.5,
      minWidth: 0,
    },
    "& .MuiSvgIcon-root": { color: "primary.main" },
  };
  const documentCardSx = {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    boxShadow: "none",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
      borderColor: "primary.main",
    },
  };

  const [deal, setDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [interestDialogOpen, setInterestDialogOpen] = useState(false);
  const [interestChoice, setInterestChoice] = useState("");
  const [termsSheetOpen, setTermsSheetOpen] = useState(false);
  const [bankerDocuments, setBankerDocuments] = useState<any[]>([]);
  const [documents, setDocuments] = useState<BankerDocumentForm[]>([
    { docName: '', type: 'upload' }
  ]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragOverUploadIndex, setDragOverUploadIndex] = useState<number | null>(null);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const snapshotRef = useRef<HTMLDivElement | null>(null);
  const businessCustomerRef = useRef<HTMLDivElement | null>(null);
  const shareStatusRef = useRef<HTMLDivElement | null>(null);
  const prospectKycRef = useRef<HTMLDivElement | null>(null);
  const businessDocumentsRef = useRef<HTMLDivElement | null>(null);
  const bankerDocumentsRef = useRef<HTMLDivElement | null>(null);

  const addDocumentField = () => {
    setDocuments([...documents, { docName: '', type: 'upload' }]);
  };

  const updateDocument = (index: number, field: string, value: any) => {
    setDocuments(prevDocuments => {
      const updatedDocuments = [...prevDocuments];
      updatedDocuments[index] = { ...updatedDocuments[index], [field]: value };
      return updatedDocuments;
    });
  };

  const removeDocumentField = (index: number) => {
    if (documents.length > 1) {
      setDocuments(documents.filter((_, i) => i !== index));
    }
  };

  const handleFileChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFileForDocument(index, file);
      event.target.value = "";
    }
  };

  const clearUploadFileForDocument = (index: number) => {
    setDocuments(prevDocuments => {
      const updatedDocuments = [...prevDocuments];
      updatedDocuments[index] = {
        ...updatedDocuments[index],
        file: undefined,
        fileName: undefined,
        existingFilePath: undefined,
        existingOriginalName: undefined,
      };
      return updatedDocuments;
    });
  };

  const getDocNameFromFile = (file: File) => {
    const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, "").trim();
    return nameWithoutExtension || "Banker document";
  };

  const validatePdfUploadFile = (file: File) => {
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      return "Only PDF files can be uploaded here.";
    }
    if (file.size > 25 * 1024 * 1024) {
      return "File size exceeds 25MB limit.";
    }
    return "";
  };

  const setUploadFileForDocument = (index: number, file: File) => {
    const validationError = validatePdfUploadFile(file);
    if (validationError) {
      setSnackbarMessage(validationError);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setDocuments(prevDocuments => {
      const updatedDocuments = [...prevDocuments];
      updatedDocuments[index] = {
        ...updatedDocuments[index],
        type: "upload",
        file,
        fileName: file.name,
        existingFilePath: undefined,
        existingOriginalName: undefined,
        docName: updatedDocuments[index].docName || getDocNameFromFile(file),
      };
      return updatedDocuments;
    });
  };

  const setExistingBankerDocumentForUpload = (index: number, sourceDocument: any) => {
    if (!sourceDocument?.filePath) {
      setSnackbarMessage("Only uploaded banker PDFs can be reused here.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setDocuments(prevDocuments => {
      const updatedDocuments = [...prevDocuments];
      updatedDocuments[index] = {
        ...updatedDocuments[index],
        type: "upload",
        file: undefined,
        fileName: sourceDocument.originalName || sourceDocument.docName,
        existingFilePath: sourceDocument.filePath,
        existingOriginalName: sourceDocument.originalName || sourceDocument.docName,
        docName: updatedDocuments[index].docName || sourceDocument.docName || "Banker document",
      };
      return updatedDocuments;
    });
  };

  const handleBankerDocumentDragStart = (doc: any, event: React.DragEvent<HTMLDivElement>) => {
    if (!doc?.filePath || doc.isCustomerUpload) return;

    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData("application/json", JSON.stringify({
      docName: doc.docName,
      filePath: doc.filePath,
      originalName: doc.originalName,
    }));
  };

  const handleUploadDragOver = (index: number, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOverUploadIndex(index);
  };

  const handleUploadDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOverUploadIndex(null);
  };

  const handleUploadDrop = (index: number, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOverUploadIndex(null);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      setUploadFileForDocument(index, file);
      return;
    }

    const draggedDocument = event.dataTransfer.getData("application/json");
    if (draggedDocument) {
      try {
        setExistingBankerDocumentForUpload(index, JSON.parse(draggedDocument));
      } catch (error) {
        setSnackbarMessage("Could not use the selected banker document.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    }
  };

  const refreshBankerDocuments = async () => {
    if (!id) return;
    const refreshedDocuments = await getBankerDocuments(id);
    setBankerDocuments(refreshedDocuments || []);
  };

  const openBankerDocumentFile = (doc: any) => {
    if (!doc?.filePath) {
      setSnackbarMessage("No banker uploaded file is available for this document.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    window.open(doc.filePath, "_blank", "noopener,noreferrer");
  };

  const handleSubmitDocuments = async () => {
    if (!id) return;

    const validDocuments = documents.filter(doc => doc.docName.trim() !== '' && (doc.type !== 'upload' || !!doc.file || !!doc.existingFilePath));

    if (validDocuments.length === 0) {
      setSnackbarMessage("Please provide at least one valid document (name required, file required for uploads).");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      const documentsJson = validDocuments.map(doc => ({
        dealId: id,
        docName: doc.docName,
        isCustomerUpload: doc.type === 'request',
        filePath: doc.existingFilePath,
        originalName: doc.existingOriginalName,
      }));
      formData.append('documents', JSON.stringify(documentsJson));
      validDocuments.forEach(doc => {
        if (doc.type === 'upload' && doc.file) {
          formData.append('files', doc.file);
        }
      });
      await createBankerDocument(formData);

      await refreshBankerDocuments();

      // Reset form
      setDocuments([{ docName: '', type: 'upload' }]);

      setSnackbarMessage("Documents processed successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error processing documents:", error);
      setSnackbarMessage("Failed to process documents.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateDealStatus = (dealId: string, status: string) => {
    // For details page, we might need to refetch or update local state if needed
    // Since it's a single deal, we can refetch the deal details
    const fetchUpdatedDeal = async () => {
      try {
        const data = await getBankerDealDetails(dealId);
        setDeal(data);
      } catch (err) {
        console.error("Error refetching deal details:", err);
      }
    };
    fetchUpdatedDeal();
  };

  const { handleInterest } = useExpressInterest(updateDealStatus);

  useEffect(() => {
    const fetchDealDetails = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await getBankerDealDetails(id);
        setDeal(data);
        // Fetch banker documents for this deal
        const documents = await getBankerDocuments(id);
        setBankerDocuments(documents);
      } catch (err: any) {
        setError(err.message || "Failed to fetch deal details");
      } finally {
        setLoading(false);
      }
    };

    fetchDealDetails();
  }, [id]);

  useEffect(() => {
    if (loading || !deal) return;
    if (window.localStorage.getItem(BANKER_DEAL_TUTORIAL_KEY)) return;
    window.localStorage.setItem(BANKER_DEAL_TUTORIAL_KEY, "true");
    setTutorialStep(0);
    setTutorialOpen(true);
  }, [deal, loading]);

  const handleInterestClick = () => {
    setInterestDialogOpen(true);
  };

  const handleDialogClose = () => {
    setInterestDialogOpen(false);
    setInterestChoice("");
  };

  const handleDialogSubmit = async () => {
    if (!deal || !id || !interestChoice) return;
    if (interestChoice === "interested") {
      setTermsSheetOpen(true);
    } else {
      const status = LoanStatus.REJECTED;
      await handleInterest(id, status);
    }
    handleDialogClose();
  };

  const handleTermsSheetAccept = async (dealId: string) => {
    await handleInterest(dealId, LoanStatus.APPROVED);
    setTermsSheetOpen(false);
  };

  const handleTermsSheetClose = () => {
    setTermsSheetOpen(false);
  };

  const showTutorial = () => {
    setTutorialStep(0);
    setTutorialOpen(true);
  };

  const closeTutorial = () => {
    setTutorialOpen(false);
  };

  const handleNextTutorialStep = () => {
    if (tutorialStep === tutorialSteps.length - 1) {
      closeTutorial();
      return;
    }
    setTutorialStep((step) => step + 1);
  };

  const handleBackTutorialStep = () => {
    setTutorialStep((step) => Math.max(step - 1, 0));
  };

  const handleDeleteDocument = (documentId: string) => {
    setDocumentToDelete(documentId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete) return;
    try {
      await deleteBankerDocument(documentToDelete);
      await refreshBankerDocuments();
      setSnackbarMessage("Document deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting document:", error);
      setSnackbarMessage("Failed to delete document.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };
  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      >
        <Loader />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert
          severity="error"
          sx={{
            borderRadius: 2,
            '& .MuiAlert-icon': { fontSize: 28 }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            Error Loading Deal
          </Typography>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!deal) {
    return (
      <Box p={4}>
        <Alert
          severity="warning"
          sx={{
            borderRadius: 2,
            '& .MuiAlert-icon': { fontSize: 28 }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            Deal Not Found
          </Typography>
          The requested deal could not be found. Please check the deal ID and try again.
        </Alert>
      </Box>
    );
  }

  const interestStatusLabel =
    deal.bankerInterestStatus === LoanStatus.APPROVED
      ? "Interested"
      : deal.bankerInterestStatus === LoanStatus.REJECTED
      ? "Not Interested"
      : "Pending Interest";
  const interestStatusColor =
    deal.bankerInterestStatus === LoanStatus.APPROVED
      ? "success"
      : deal.bankerInterestStatus === LoanStatus.REJECTED
      ? "error"
      : "warning";
  const businessName = `${(deal.business as any)?.name || "-"}`;
  const customerName = `${(deal.customer as any)?.firstName || ""} ${
    (deal.customer as any)?.middleName || ""
  } ${(deal.customer as any)?.lastName || ""}`.trim();
  const businessDocumentCount = deal.businessDocuments?.length || 0;
  const tutorialSteps: GuidedTutorialStep[] = [
    {
      title: "Review Deal Header",
      description:
        "Start here to confirm the loan name, business name, current interest status, and express interest action.",
      targetRef: headerRef,
    },
    {
      title: "Check Deal Snapshot",
      description:
        "Use this summary to quickly compare deal type, requested loan size, max LTV, and minimum DSC ratio before going deeper.",
      targetRef: snapshotRef,
    },
    {
      title: "Review Business and Customer",
      description:
        "This section shows the business attached to the deal, customer name when shared, and the deal creation date.",
      targetRef: businessCustomerRef,
    },
    {
      title: "Customer Sharing Status",
      description:
        "This message tells you whether customer details are shared. If details are hidden, you can still request documents from the customer.",
      targetRef: shareStatusRef,
    },
    ...(deal.prospectKyc && deal.isShareCustomerDetails
      ? [
          {
            title: "Review Prospect KYC",
            description:
              "Use the KYC section to review identity, contact, address, income, and business revenue details shared by the customer.",
            targetRef: prospectKycRef,
          },
        ]
      : []),
    {
      title: "Review Business Documents",
      description:
        "Business documents uploaded by the customer appear here. Open each file to verify supporting information for the deal.",
      targetRef: businessDocumentsRef,
    },
    {
      title: "Request Banker Documents",
      description:
        "Add banker-required documents here. You can upload a file yourself or request the customer to upload it from their side.",
      targetRef: bankerDocumentsRef,
    },
  ];

  return (
    <Box sx={{ p: { xs: 0, sm: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100%" }}>
      <Card
        sx={{
          borderRadius: { xs: 0, sm: 3 },
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
          background: "background.paper",
          overflow: "hidden",
        }}
      >
        <Box
          ref={headerRef}
          sx={{
            p: { xs: 2, sm: 3 },
            background:
              "linear-gradient(135deg, #eff6ff 0%, #ffffff 48%, #f8fafc 100%)",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                color: "text.secondary",
                px: 0,
                '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
              }}
            >
              Back to Deals
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1.5 : 2, minWidth: 0 }}>
              <Box
                sx={{
                  width: { xs: 42, sm: 52 },
                  height: { xs: 42, sm: 52 },
                  borderRadius: 2,
                  background: "linear-gradient(135deg, #2563eb, #0f766e)",
                  color: "primary.contrastText",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  boxShadow: "0 12px 24px rgba(37,99,235,0.22)",
                }}
              >
                <BusinessIcon sx={{ fontSize: isMobile ? 24 : 30 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 800, color: "text.primary", overflowWrap: "anywhere" }}>
                  {deal.loanName}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  {businessName}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, width: { xs: "100%", sm: "auto" } }}>
              <Chip
                label={interestStatusLabel}
                color={interestStatusColor as any}
                sx={{ fontWeight: 800, height: 36 }}
              />
              <Button
                variant="outlined"
                startIcon={<HelpOutlineIcon />}
                onClick={showTutorial}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 800,
                  width: { xs: "100%", sm: "auto" },
                  bgcolor: "background.paper",
                }}
              >
                Show Tutorial
              </Button>
              <Button
                variant="contained"
                startIcon={<ThumbUpIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 800,
                  width: { xs: "100%", sm: "auto" },
                  boxShadow: "0 10px 20px rgba(37,99,235,0.22)",
                }}
                onClick={handleInterestClick}
              >
                {deal.bankerInterestStatus === LoanStatus.APPROVED
                  ? "Interested"
                  : deal.bankerInterestStatus === LoanStatus.REJECTED
                  ? "Not Interested"
                  : "Express Interest"}
              </Button>
            </Box>
          </Box>
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box ref={snapshotRef} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 900, color: 'text.primary' }}>
              Deal Snapshot
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2
            }}>
              <MetricCard
                icon={<RequestQuoteIcon />}
                label="Type"
                value={deal.dealType}
                tone="slate"
              />
              <MetricCard
                icon={<AttachMoneyIcon />}
                label="Loan Size"
                value={`${formatCurrency(deal.loanSize)}`}
                tone="green"
              />
              <MetricCard
                icon={<PercentIcon />}
                label="Max LTV"
                value={`${deal.maxLTV}%`}
                tone="blue"
              />
              <MetricCard
                icon={<AssessmentIcon />}
                label="Min DSC Ratio"
                value={deal.minDSCRatio?.toString() || "N/A"}
                tone="amber"
              />
            </Box>
          </Box>

          <Box ref={businessCustomerRef} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 900, color: 'text.primary' }}>
              Business & Customer
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2
            }}>
              <InfoItem
                icon={<AccountBalanceIcon color="primary" />}
                label="Business Name"
                value={businessName}
                tooltip="Business Name"
              />
              {deal.isShareCustomerDetails && (
                <InfoItem
                  icon={<PersonIcon color="primary" />}
                  label="Customer Name"
                  value={customerName || "N/A"}
                  tooltip="Customer Name"
                />
              )}
              <InfoItem
                icon={<CalendarTodayIcon color="action" />}
                label="Created At"
                value={deal.createdAt ? new Date(deal.createdAt).toLocaleDateString() : "N/A"}
                tooltip="Deal Creation Date"
              />
            </Box>
          </Box>
          <Divider sx={{ mb: 2.5 }} />

          <Alert
            ref={shareStatusRef}
            severity={deal.isShareCustomerDetails ? "success" : "warning"}
            sx={{
              mb: 2.5,
              borderRadius: 2,
              alignItems: "center",
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              {deal.isShareCustomerDetails
                ? "Customer details are shared for this deal."
                : "Customer details are not shared for this deal."}
            </Typography>
            <Typography variant="body2">
              {deal.isShareCustomerDetails
                ? "Review KYC, business documents, and banker document requests from the sections below."
                : "You can still review the deal snapshot and request banker documents from the customer."}
            </Typography>
          </Alert>

          {/* Customer Details */}
          {/* {deal.isShareCustomerDetails && (
            <Accordion
              sx={{
                mb: 3,
                borderRadius: 2,
                '&:before': { display: 'none' },
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderRadius: 2,
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                <PersonIcon sx={{ mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Customer Details
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 2
                }}>
                  <InfoItem
                    icon={<PersonIcon color="primary" />}
                    label="Name"
                    value={`${(deal.customer as any)?.firstName || ''} ${(deal.customer as any)?.middleName || ''} ${(deal.customer as any)?.lastName || ''}`.trim() || deal.customer?.name || "N/A"}
                    tooltip="Customer Name"
                  />
                  <InfoItem
                    icon={<EmailIcon color="secondary" />}
                    label="Email"
                    value={deal.customer?.email || "N/A"}
                    tooltip="Customer Email"
                  />
                  <InfoItem
                    icon={<PhoneIcon color="success" />}
                    label="Phone"
                    value={formatPhoneNumber(deal.customer?.phone) || "N/A"}
                    tooltip="Customer Phone"
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          )} */}

          {/* Prospect KYC */}
          {deal.prospectKyc && deal.isShareCustomerDetails && (
            <Accordion ref={prospectKycRef} defaultExpanded sx={sectionAccordionSx}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={sectionSummarySx}
              >
                <BusinessIcon />
                <Typography variant="h6" sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>
                  Prospect KYC
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 2
                }}>
                  <InfoItem
                    icon={<PersonIcon color="primary" />}
                    label="Full Name"
                    value={`${(deal.customer as any)?.firstName || ''} ${(deal.customer as any)?.middleName || ''} ${(deal.customer as any)?.lastName || ''}`.trim() || "N/A"}
                    tooltip="Prospect Full Name"
                  />
                  <InfoItem
                    icon={<CalendarTodayIcon color="secondary" />}
                    label="DOB"
                    value={deal.customer.dob || "N/A"}
                    tooltip="Date of Birth"
                  />
                  <InfoItem
                    icon={<LocationOnIcon color="info" />}
                    label="Address"
                    value={[
                      deal.customer.street,
                      deal.customer.city,
                      deal.customer.state,
                      deal.customer.zipcode,
                    ].filter(Boolean).join(", ") || "N/A"}
                    tooltip="Prospect Address"
                  />
                  <InfoItem
                    icon={<EmailIcon color="secondary" />}
                    label="Email"
                    value={deal.customer.email || "N/A"}
                    tooltip="Prospect Email"
                  />
                  <InfoItem
                    icon={<PhoneIcon color="success" />}
                    label="Phone"
                    value={formatPhoneNumber(deal.customer.phone) || "N/A"}
                    tooltip="Prospect Phone"
                  />
                  <InfoItem
                    icon={<AttachMoneyIcon color="secondary" />}
                    label="Personal Income"
                    value={formatCurrency(deal.prospectKyc.personalIncome) || "N/A"}
                    tooltip="Personal Income"
                  />
                  <InfoItem
                    icon={<AttachMoneyIcon color="success" />}
                    label="Business Revenue"
                    value={formatCurrency(deal.prospectKyc.businessRevenue) || "N/A"}
                    tooltip="Business Revenue"
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Business Documents */}
          <Accordion ref={businessDocumentsRef} defaultExpanded sx={sectionAccordionSx}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={sectionSummarySx}
            >
              <DescriptionIcon />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexWrap: "wrap" }}>
                <Typography variant="h6" sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>
                  Business Documents
                </Typography>
                <Chip
                  label={`${businessDocumentCount} file${businessDocumentCount === 1 ? "" : "s"}`}
                  size="small"
                  color={businessDocumentCount > 0 ? "primary" : "default"}
                  sx={{ fontWeight: 700 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: { xs: 2, sm: 3 } }}>
              {businessDocumentCount === 0 ? (
                <Box
                  sx={{
                    p: 3,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    textAlign: "center",
                  }}
                >
                  <DescriptionIcon sx={{ color: "text.secondary", mb: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    No business documents uploaded yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Uploaded customer business files will appear here for review.
                  </Typography>
                </Box>
              ) : (
                Object.entries(
                  (deal.businessDocuments || []).reduce((acc, doc) => {
                    const docs = acc[doc.docName] ?? [];
                    docs.push(doc);
                    acc[doc.docName] = docs;
                    return acc;
                  }, {} as Record<string, NonNullable<typeof deal.businessDocuments>>)
                ).map(([docName, docs]) => (
                  <Box key={docName} sx={{ mb: 4 }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: { xs: "flex-start", sm: "center" },
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1,
                      mb: 3,
                      p: 2,
                      bgcolor: '#f8fafc',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}>
                      <DescriptionIcon sx={{ color: 'primary.main' }} />
                      <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', overflowWrap: "anywhere" }}>
                        {docName}
                      </Typography>
                      <Chip
                        label={`${docs.length} file${docs.length > 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                          fontWeight: 700
                        }}
                      />
                    </Box>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                      gap: 2
                    }}>
                      {docs.map((doc) => (
                        <Tooltip key={doc.id} title="Click to view document">
                          <Card
                            sx={{ ...documentCardSx, cursor: 'pointer' }}
                            onClick={() => openBusinessDocumentFile(doc.id)}
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <DescriptionIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 'bold',
                                    color: 'text.primary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {doc.originalName}
                                </Typography>
                              </Box>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Click to view document
                              </Typography>
                            </CardContent>
                          </Card>
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                ))
              )}
            </AccordionDetails>
          </Accordion>

          {/* Banker Documents Required for Loan to Send Customer */}
          <Accordion ref={bankerDocumentsRef} defaultExpanded sx={{ ...sectionAccordionSx, mb: 0 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={sectionSummarySx}
            >
              <DescriptionIcon />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>
                  Banker Document Requests
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload files yourself or ask the customer to provide them.
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: { xs: 2, sm: 3 } }}>
              {bankerDocuments.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'text.primary' }}>
                    Banker Documents
                  </Typography>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: 2
                  }}>
                    {bankerDocuments.map((doc) => (
                      <Card
                        key={doc.id}
                        sx={documentCardSx}
                        draggable={!doc.isCustomerUpload && !!doc.filePath}
                        onDragStart={(event) => handleBankerDocumentDragStart(doc, event)}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <DescriptionIcon sx={{ color: doc.isCustomerUpload ? 'warning.main' : 'primary.main', fontSize: 20 }} />
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 'bold',
                                color: 'text.primary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {doc.docName}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: "block", overflowWrap: "anywhere" }}>
                            {doc.originalName || (doc.isCustomerUpload ? "Waiting for customer upload" : "Uploaded banker document")}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', flexWrap: "wrap", justifyContent: isMobile ? 'flex-start' : 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: 1, mt: 1.5 }}>
                            {!doc.isCustomerUpload && (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => openBankerDocumentFile(doc)}
                                  sx={{
                                    textTransform: 'none',
                                    borderRadius: 1,
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  View
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => setExistingBankerDocumentForUpload(0, doc)}
                                  sx={{
                                    textTransform: 'none',
                                    borderRadius: 1,
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  Use
                                </Button>
                              </>
                            )}
                            {doc.isCustomerUpload && (
                              <Chip
                                label="Requires Customer Upload"
                                color="warning"
                                size="small"
                                sx={{ fontSize: '0.75rem' }}
                              />
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleDeleteDocument(doc.id)}
                              sx={{
                                textTransform: 'none',
                                borderRadius: 1,
                                fontSize: '0.75rem'
                              }}
                            >
                              Delete
                            </Button>
                            {doc?.customerFilePath && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => window.open(`${doc.customerFilePath}`, '_blank', 'noopener,noreferrer')}
                                sx={{
                                  textTransform: 'none',
                                  borderRadius: 1,
                                  fontSize: '0.75rem'
                                }}
                              >
                                View Customer Docs
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}
              {bankerDocuments.length === 0 && (
                <Box
                  sx={{
                    mb: 3,
                    p: 3,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    textAlign: "center",
                  }}
                >
                  <DescriptionIcon sx={{ color: "text.secondary", mb: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    No banker documents added yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Uploaded banker PDFs and customer document requests will appear here.
                  </Typography>
                </Box>
              )}

              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                Use upload when you already have the file. Use request when the customer needs to upload it from their side.
              </Alert>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {documents.map((doc, index) => (
                  <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: { xs: 1.5, sm: 2 }, border: '1px solid', borderColor: 'divider', borderLeft: "4px solid", borderLeftColor: doc.type === "upload" ? "primary.main" : "warning.main", borderRadius: 2, bgcolor: "#fff" }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: "flex-start", sm: "center" }, gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        Document {index + 1}
                      </Typography>
                      {documents.length > 1 && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => removeDocumentField(index)}
                          sx={{ textTransform: 'none' }}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                    <TextField
                      label="Document Name"
                      variant="outlined"
                      fullWidth
                      placeholder="Enter document name"
                      value={doc.docName}
                      onChange={(e) => updateDocument(index, 'docName', e.target.value)}
                      InputProps={{
                        style: { borderRadius: 10 },
                      }}
                    />
                    {doc.type === "upload" && (
                      <Box
                        sx={{
                          p: 2,
                          border: "2px dashed",
                          borderColor: dragOverUploadIndex === index ? "primary.main" : doc.fileName ? "success.main" : "divider",
                          borderRadius: 2,
                          bgcolor: dragOverUploadIndex === index ? "#eff6ff" : doc.fileName ? "#ecfdf5" : "#f8fafc",
                          cursor: "pointer",
                          transition: "background-color 0.2s ease, border-color 0.2s ease",
                          "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: "#eff6ff",
                          },
                        }}
                        onClick={() => document.getElementById(`document-file-${index}`)?.click()}
                        onDragOver={(event) => handleUploadDragOver(index, event)}
                        onDragLeave={handleUploadDragLeave}
                        onDrop={(event) => handleUploadDrop(index, event)}
                      >
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          style={{ display: 'none' }}
                          id={`document-file-${index}`}
                          onChange={(e) => handleFileChange(index, e)}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                          <CloudUploadIcon sx={{ color: doc.fileName ? "success.main" : "primary.main", flexShrink: 0 }} />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: "text.primary" }}>
                              {dragOverUploadIndex === index ? "Drop PDF here" : doc.fileName ? "PDF selected" : "Upload PDF"}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {doc.fileName || "Click to choose, drop a PDF, or drop a banker document above"}
                            </Typography>
                          </Box>
                          {doc.fileName && (
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={(event) => {
                                event.stopPropagation();
                                clearUploadFileForDocument(index);
                              }}
                              sx={{
                                borderRadius: 1,
                                textTransform: "none",
                                fontSize: "0.75rem",
                                flexShrink: 0,
                              }}
                            >
                              Cancel
                            </Button>
                          )}
                        </Box>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1.5 }}>
                      <Button
                        variant={doc.type === "upload" ? "contained" : "outlined"}
                        onClick={() => updateDocument(index, 'type', 'upload')}
                        startIcon={<DescriptionIcon />}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          flex: 1,
                          boxShadow: "none",
                        }}
                      >
                        Upload File
                      </Button>
                      <Button
                        variant={doc.type === "request" ? "contained" : "outlined"}
                        onClick={() => updateDocument(index, 'type', 'request')}
                        startIcon={<PersonIcon />}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          flex: 1,
                          boxShadow: "none",
                        }}
                      >
                        Request Customer Upload
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {doc.type === "upload"
                        ? "Attach the document now so it is immediately available on this deal."
                        : "The customer will see this as a required upload request."}
                    </Typography>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  onClick={addDocumentField}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    alignSelf: { xs: "stretch", sm: "flex-start" },
                  }}
                >
                  Add Another Document
                </Button>
                <Button
                  variant="contained"
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : <DescriptionIcon />}
                  onClick={handleSubmitDocuments}
                  disabled={documents.filter(doc => doc.docName.trim() !== '' && (doc.type !== 'upload' || !!doc.file || !!doc.existingFilePath)).length === 0 || isSubmitting}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: "none",
                    alignSelf: { xs: "stretch", sm: "flex-start" },
                    '&:hover': { boxShadow: "0 8px 18px rgba(37,99,235,0.22)" },
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Documents'}
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      <GuidedTutorialOverlay
        open={tutorialOpen}
        steps={tutorialSteps}
        activeStep={tutorialStep}
        onBack={handleBackTutorialStep}
        onNext={handleNextTutorialStep}
        onClose={closeTutorial}
      />

      <ExpressInterestDialog
        open={interestDialogOpen}
        onClose={handleDialogClose}
        dealName={deal.loanName}
        interestChoice={interestChoice}
        onInterestChoiceChange={setInterestChoice}
        onSubmit={handleDialogSubmit}
      />

      <TermsSheet
        open={termsSheetOpen}
        deal={deal}
        onClose={handleTermsSheetClose}
        onAccept={handleTermsSheetAccept}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={confirmDeleteDocument}
        title="Delete Document"
        description="Are you sure you want to delete this document? This action cannot be undone."
      />
    </Box>
  );
};

export default BankerDealDetails;
